import type { Schema } from 'yup'
import { object, string, number } from 'yup'

type RateSchemaKeys = 'name' | 'description' | 'price' | 'min' | 'max'

export function rateSchema(fields: RateSchemaKeys[], t: (key: string, params?: any) => string) {
  const baseSchema = {
    name: string().required(t('errors.required')),
    description: string().required(t('errors.required')),
    price: number()
      .required(t('errors.required'))
      .min(0, t('errors.minValue', { min: 0 })),
    min: number()
      .required(t('errors.required'))
      .min(1, t('errors.minValue', { min: 1 }))
      .integer(t('errors.integer')),
    max: number()
      .nullable()
      .integer(t('errors.integer'))
      .test('max-greater-than-min', t('errors.maxGreaterThanMin'), function (value) {
        return !value || value >= this.parent.min
      }),
  }

  const partialSchema: Partial<Record<RateSchemaKeys, Schema<any>>> = {}
  fields.forEach((fieldName) => {
    if (baseSchema[fieldName]) {
      partialSchema[fieldName] = baseSchema[fieldName]
    }
  })
  return object(partialSchema)
}

export const useRateStore = defineStore('rate', () => {
  const rates = ref<any[]>([])

  async function fetchSlotRates(calendarId: number, slotId: number) {
    try {
      rates.value = await $fetch(`/api/calendar/${calendarId}/slot/${slotId}/rates`)
      return { data: rates.value, status: 'success', error: null }
    } catch (e: any) {
      return { data: null, status: 'error', error: e.data || e }
    }
  }

  async function createRate(rateData: any) {
    try {
      const result = await $fetch(`/api/calendar/${rateData.calendarId}/slot/${rateData.slotId}/rate`, {
        method: 'POST',
        body: rateData,
      })

      if (result) {
        rates.value.push(result)
      }
      return { data: result, status: 'success', error: null }
    } catch (e: any) {
      return { data: null, status: 'error', error: e.data || e }
    }
  }

  async function updateRate(calendarId: number, rateId: number, rateData: any) {
    try {
      const result = await $fetch(`/api/calendar/${calendarId}/rate/${rateId}`, {
        method: 'PUT',
        body: rateData,
      })

      if (result) {
        const index = rates.value.findIndex(r => r.id === rateId)
        if (index !== -1) {
          rates.value[index] = result
        }
      }
      return { data: result, status: 'success', error: null }
    } catch (e: any) {
      return { data: null, status: 'error', error: e.data || e }
    }
  }

  async function deleteRate(calendarId: number, rateId: number) {
    try {
      const result = await $fetch(`/api/calendar/${calendarId}/rate/${rateId}`, {
        method: 'DELETE',
      })

      if (result) {
        rates.value = rates.value.filter(r => r.id !== rateId)
      }
      return { data: result, status: 'success', error: null }
    } catch (e: any) {
      return { data: null, status: 'error', error: e.data || e }
    }
  }

  return {
    rates,
    fetchSlotRates,
    createRate,
    updateRate,
    deleteRate,
  }
})
